/**
 * CustomerReviewsTab — what customers think of the workshop.
 *
 * Ten questions, each rated 1-10 by customers after they collect their vehicle. Shows
 * the average per question plus a headline score, over a date window.
 *
 * A TAB on the Customers screen, not its own nav item. Reviews are about the same people
 * the other two tabs manage, and a fourth top-level entry for one read-only panel makes
 * the sidebar longer without making anything easier to find. The parent owns the
 * <Layout>, the page header and the tab strip; this file renders only the panel.
 *
 * READING THE NUMBERS
 * -------------------
 * Every figure here is this workshop's own — scoped server-side to the organisation on
 * the caller's token.
 *
 * Each question carries its OWN response count, not one shared total. A question
 * enabled part-way through the window legitimately has fewer answers than one that has
 * been asked all along, and a single denominator would misreport every row.
 *
 * The headline is the mean of every individual rating, not the mean of the ten
 * averages. Those agree until the counts differ, and then only the first is honest —
 * a question with three answers should not swing the headline like one with three
 * hundred.
 *
 * WHAT IS NOT DERIVED HERE
 * ------------------------
 * The averages and the headline are computed server-side and rendered as given. This
 * page must never recompute them: the weighting rule belongs to the backend, and a
 * second copy here would drift the moment either changes.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { MessageSquare, AlertCircle, Loader2, Calendar } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
    fetchFeedbackSummary,
    selectFeedbackQuestions,
    selectFeedbackOverallScore,
    selectFeedbackResponseCount,
    selectFeedbackLoading,
    selectFeedbackError,
} from '../store/slices/customerFeedbackSlice';
import { today, shiftDay, shortDay } from '../utils/dayRange';

/*
 * Windows offered on the page. Longer than the phone app's 7 days on purpose: feedback
 * arrives at a trickle — one response per collected vehicle, only from customers who
 * bother — so a week is often an empty page even at a busy workshop.
 */
const RANGES = [
    { key: '30',  label: '30 days',  days: 30  },
    { key: '90',  label: '90 days',  days: 90  },
    { key: '365', label: '12 months', days: 365 },
];

const DEFAULT_RANGE = '90';

/** A score out of 10, or an em dash. NEVER 0.0 — that is not on the scale. */
const score = (value) => (value === null || value === undefined ? '—' : value.toFixed(1));

/** Green above 8, amber from 6, red below — the same reading a workshop owner makes. */
const scoreTone = (value) => {
    if (value === null || value === undefined) return 'text-gray-400';
    if (value >= 8) return 'text-emerald-600';
    if (value >= 6) return 'text-amber-600';
    return 'text-red-600';
};

const barTone = (value) => {
    if (value === null || value === undefined) return 'bg-gray-200';
    if (value >= 8) return 'bg-emerald-500';
    if (value >= 6) return 'bg-amber-500';
    return 'bg-red-500';
};

// ── One question row ─────────────────────────────────────────────────────────

const QuestionRow = ({ question }) => {
    const { label, enabled, averageRating, responseCount, labelChangedAt } = question;
    const answered = averageRating !== null && averageRating !== undefined;

    return (
        <li className={`px-4 py-3 sm:px-6 ${enabled ? '' : 'bg-gray-50'}`}>
            <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                    <p className={`text-sm font-medium ${enabled ? 'text-gray-900' : 'text-gray-500'}`}>
                        {label}
                        {/* A disabled question keeps its history — turning it off stops
                            it being asked, it does not erase what customers said. */}
                        {!enabled && (
                            <span className="ml-2 rounded-full bg-gray-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-600">
                                Not asked
                            </span>
                        )}
                    </p>

                    {/* Marks where the series stops being continuous. Answers key on the
                        slot, so a reworded question shows today's label over answers
                        given to the old one — this says so rather than hiding it. */}
                    {labelChangedAt && (
                        <p className="mt-0.5 text-[11px] text-amber-700">
                            Wording changed {shortDay(labelChangedAt.slice(0, 10))} — earlier
                            responses answered a different question
                        </p>
                    )}

                    <div className="mt-2 h-1.5 w-full max-w-md overflow-hidden rounded-full bg-gray-100">
                        <div
                            className={`h-full rounded-full ${barTone(averageRating)}`}
                            style={{ width: answered ? `${(averageRating / 10) * 100}%` : '0%' }}
                        />
                    </div>
                </div>

                <div className="shrink-0 text-right">
                    <p className={`text-lg font-bold ${scoreTone(averageRating)}`}>
                        {score(averageRating)}
                        {answered && <span className="text-xs font-medium text-gray-400">/10</span>}
                    </p>
                    <p className="text-[11px] text-gray-500">
                        {responseCount === 0
                            ? 'No responses'
                            : `${responseCount} ${responseCount === 1 ? 'response' : 'responses'}`}
                    </p>
                </div>
            </div>
        </li>
    );
};

// ── Page ─────────────────────────────────────────────────────────────────────

const CustomerReviewsTab = () => {
    const dispatch = useAppDispatch();

    const questions = useAppSelector(selectFeedbackQuestions);
    const overallScore = useAppSelector(selectFeedbackOverallScore);
    const responseCount = useAppSelector(selectFeedbackResponseCount);
    const loading = useAppSelector(selectFeedbackLoading);
    const error = useAppSelector(selectFeedbackError);

    const [range, setRange] = useState(DEFAULT_RANGE);

    const load = useCallback((rangeKey) => {
        const days = RANGES.find((r) => r.key === rangeKey)?.days ?? 90;
        const to = today();
        // Local-time throughout — utils/dayRange never touches toISOString(), which
        // returns yesterday for every Australian user before 10am.
        dispatch(fetchFeedbackSummary({ from: shiftDay(to, -(days - 1)), to }));
    }, [dispatch]);

    useEffect(() => {
        load(DEFAULT_RANGE);
    }, [load]);

    const pickRange = (key) => {
        setRange(key);
        load(key);
    };

    const activeRange = RANGES.find((r) => r.key === range);

    return (
        <div>
            {/* Range picker */}
                <div className="mb-4 flex items-center gap-1 rounded-xl border border-gray-200 bg-white p-1">
                    {RANGES.map(({ key, label }) => (
                        <button
                            key={key}
                            type="button"
                            disabled={loading}
                            aria-pressed={range === key}
                            onClick={() => pickRange(key)}
                            className={`flex-1 rounded-lg py-2 text-sm font-semibold transition-colors disabled:opacity-50 ${
                                range === key ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'
                            }`}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                {error && (
                    <div className="mb-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3">
                        <AlertCircle size={16} className="mt-0.5 shrink-0 text-red-500" />
                        <div>
                            {/* The server's message verbatim — it names which date is wrong. */}
                            <p className="text-sm text-red-700">{error}</p>
                            <button
                                type="button"
                                onClick={() => load(range)}
                                className="mt-2 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white"
                            >
                                Try again
                            </button>
                        </div>
                    </div>
                )}

                {/* Headline */}
                <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="rounded-2xl border border-gray-200 bg-white px-5 py-4">
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                            Overall score
                        </p>
                        <p className={`mt-1 text-3xl font-bold ${scoreTone(overallScore)}`}>
                            {score(overallScore)}
                            {overallScore !== null && overallScore !== undefined && (
                                <span className="text-base font-medium text-gray-400">/10</span>
                            )}
                        </p>
                        <p className="mt-1 flex items-center gap-1 text-xs text-gray-500">
                            <Calendar size={11} />
                            {activeRange?.label}
                        </p>
                    </div>

                    <div className="rounded-2xl border border-gray-200 bg-white px-5 py-4">
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                            Responses
                        </p>
                        <p className="mt-1 text-3xl font-bold text-gray-900">{responseCount}</p>
                        <p className="mt-1 text-xs text-gray-500">
                            customers rated you in this period
                        </p>
                    </div>
                </div>

                {/* Questions */}
                <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
                    {loading && questions.length === 0 ? (
                        <div className="flex justify-center py-16">
                            <Loader2 size={24} className="animate-spin text-emerald-600" />
                        </div>
                    ) : questions.length === 0 ? (
                        <div className="flex flex-col items-center justify-center px-8 py-16 text-center">
                            <MessageSquare size={28} className="text-gray-300" />
                            <p className="mt-3 text-sm font-medium text-gray-900">
                                No reviews in this period
                            </p>
                            <p className="mt-1 text-xs text-gray-500">
                                Customers are invited to rate you when their vehicle is collected.
                            </p>
                        </div>
                    ) : (
                        <ul className="divide-y divide-gray-100">
                            {questions.map((q) => (
                                <QuestionRow key={q.slotNumber} question={q} />
                            ))}
                        </ul>
                    )}
            </div>
        </div>
    );
};

export default CustomerReviewsTab;
